import React from 'react';
import { FormattedMessage } from 'react-intl';
import { of, EMPTY, concat, merge } from 'rxjs';
import { ofType } from 'redux-observable';
import { catchError, mergeMap, takeUntil, switchMap, tap, take } from 'rxjs/operators';
import get from 'lodash.get';
import { ModalService } from 'ui-components/services/modal-service';
import Global from '../../../store/global-selectors';
import * as Actions from '../../../store/bulk/purchase/bulk.purchase.actions';
import * as BulkApi from '../../../store/bulk/bulk.api';
import * as BulkActions from '../../../store/bulk/bulk.actions';
import * as SelectedOrdersActions from '../../../store/orders/selected-orders/orders.selected-orders.actions';
import { trackBulkLabelCreationStart } from '../../../store/labels/tracking-events';
import { BulkPurchaseNotificationService } from '../../../services/bulk-purchase-noto-service/';
import { BulkErrorsAndWarningsService } from '../../../services/bulk-errors-and-warnings-service';
import { emptyArray } from 'ui-components/default-props/empty';
import { NotificationService } from 'ui-components/services/notification-service';

import { LabelBatchStatusStep } from '../../../enums/label-batch-status-step';

import { makeBulkCreateLabelObservable } from '../bulk.label.observables';
import { startBulkPurchaseValidation, retryValidation, waitForEndOfValidation } from './bulk.purchase.validation.observables';
import {
  applyCostReviewRecommendations,
  startBulkPurchaseCostReview,
  waitForEndOfCostReview,
} from './bulk.purchase.cost-review.observables';
import { waitForEndOfBulkPurchase, handleBulkPurchaseEnd } from './bulk.purchase.observables';
import { handleBulkPurchaseProcess } from './bulk.purchase.processing.helpers';
import { getQuickshipPropsFromState, shouldLaunchLbsModal } from '../../quickship/quickship.helpers';
import { messages } from './messages';

const showProcessingBulkOrdersNotification = (count = 0, dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.processingBulkLabelsTitle} />,
    content: <FormattedMessage {...messages.processingBulkLabelsContent} values={{ count }} />,
    notificationType: 'info',
    dontHide,
  };
  return NotificationService.showNotification({ ...props });
};

export const startBulkPurchase = (action$, { getState }) => {
  let processingNotificationId = '';

  return action$.pipe(
    ofType(Actions.BULK_PURCHASE_START),
    switchMap(({ fulfillmentPlanIds = [], andPrint = false }) => {
      const state = getState();
      const quickshipProps = getQuickshipPropsFromState(state);
      let appliedSavings = {};

      if (!fulfillmentPlanIds.length) {
        return of(
          BulkActions.bulkSetActiveLabelBatchStatusStep({
            step: LabelBatchStatusStep.NoOrdersIncluded.ordinal,
          }),
        );
      }

      const sellerLabelProviderIds = fulfillmentPlanIds.map((fulfillmentPlanId) => {
        return Global.selectEntitiesFulfillmentPlanSellerLabelProviderID(state, { fulfillmentPlanId });
      });
      const hasWalletProvider = sellerLabelProviderIds.some((sellerLabelProviderId) => {
        return Global.selectSellerLabelProviderIsWalleted(state, {
          sellerLabelProviderId,
        });
      });
      trackBulkLabelCreationStart(fulfillmentPlanIds, hasWalletProvider);

      if (!shouldLaunchLbsModal(quickshipProps)) {
        NotificationService.removeNotification(processingNotificationId);
        processingNotificationId = showProcessingBulkOrdersNotification(fulfillmentPlanIds.length);
      }

      const onRecommendationsAppliedSuccessfully = (amountSaved) => {
        appliedSavings = amountSaved;
        return of(Actions.retryValidation(fulfillmentPlanIds));
      };

      return merge(
        of(
          BulkActions.bulkSetActiveLabelBatchStatusStep({
            step: LabelBatchStatusStep.Validating.ordinal,
            totalLabels: fulfillmentPlanIds.length,
            andPrint,
          }),
        ),
        BulkApi.canPurchaseLabel(fulfillmentPlanIds).pipe(
          mergeMap((data) => {
            const activeLabelStatus = data || emptyArray;
            const included = [];
            const excluded = [];

            activeLabelStatus.forEach((s) =>
              s.canPurchaseLabel ? included.push(s.fulfillmentPlanId) : excluded.push(s.fulfillmentPlanId),
            );

            if (included.length === 0) {
              return of(Actions.endBulkPurchase(included, excluded, [], andPrint));
            }

            return merge(
              startBulkPurchaseValidation({
                action$,
                included,
                excluded,
                andPrint,
                getState,
                quickshipProps,
              }),
              retryValidation(action$).pipe(
                takeUntil(action$.pipe(ofType(Actions.BULK_PURCHASE_END))),
                switchMap((retryAction) => {
                  const newIncluded = retryAction.includedFulfillmentPlanIds || included;
                  if (!newIncluded.length) {
                    return of(
                      BulkActions.bulkSetActiveLabelBatchStatusStep({
                        step: LabelBatchStatusStep.NoOrdersIncluded.ordinal,
                      }),
                      SelectedOrdersActions.setSelectedOrders(newIncluded),
                    );
                  }
                  return merge(
                    of(
                      BulkActions.bulkSetActiveLabelBatchStatusStep({
                        step: LabelBatchStatusStep.Validating.ordinal,
                      }),
                      SelectedOrdersActions.setSelectedOrders(newIncluded),
                    ),
                    startBulkPurchaseValidation({
                      action$,
                      included: newIncluded,
                      excluded,
                      andPrint,
                      getState,
                      quickshipProps,
                    }),
                  );
                }),
              ),
            );
          }),
          catchError(() => {
            NotificationService.removeNotification(processingNotificationId);
            processingNotificationId = '';
            BulkPurchaseNotificationService.showGenericHttpNotification();
            return EMPTY;
          }),
        ),
        applyCostReviewRecommendations(action$, getState, onRecommendationsAppliedSuccessfully),
        waitForEndOfValidation(action$).pipe(
          takeUntil(action$.pipe(ofType(Actions.BULK_PURCHASE_END))),
          mergeMap((endValidationAction) => {
            const jobGuid = endValidationAction?.jobGuid;
            const bulkJob = Global.selectBulkJobByJobGuid(getState(), { jobGuid }) ?? {};
            const included = endValidationAction?.includedFulfillmentPlanIds ?? bulkJob.included ?? emptyArray;
            const excluded = endValidationAction?.excludedFulfillmentPlanIds ?? bulkJob.excluded ?? emptyArray;
            const errored = endValidationAction?.erroredFulfillmentPlanIds ?? bulkJob.errored ?? emptyArray;
            if (included.length === 0 && excluded.length === 0 && errored.length > 0) {
              // just hits reducer
              return of(BulkActions.validationEverythingIsAnError());
            }

            return merge(
              of(SelectedOrdersActions.setSelectedOrders(included)),
              startBulkPurchaseCostReview({
                action$,
                included,
                excluded,
                errored,
                andPrint,
                getState,
                quickshipProps,
                shouldAlwaysShowCostReviewDefault: true,
                appliedSavings,
              }),
            );
          }),
        ),
        waitForEndOfCostReview(action$).pipe(
          takeUntil(action$.pipe(ofType(Actions.BULK_PURCHASE_END, Actions.BULK_PURCHASE_COST_REVIEW_CANCEL), take(1))),
          mergeMap(({ jobGuid }) => {
            const costReview = Global.selectBulkJobByJobGuid(getState(), {
              jobGuid,
            });
            const included = get(costReview, 'included');
            const excluded = get(costReview, 'excluded');
            const errored = get(costReview, 'errored');

            NotificationService.removeNotification(processingNotificationId);
            processingNotificationId = '';
            const stateShipDate = Global.selectBulkShipDate(getState());
            const shipDate = stateShipDate ? new Date(stateShipDate) : new Date();
            const shipDateAsUTCMidnight = new Date(Date.UTC(shipDate.getFullYear(), shipDate.getMonth(), shipDate.getDate()));

            const lbsModalId = Global.selectBulkLbsModalId(getState());

            return concat(
              of(
                BulkActions.bulkSetActiveLabelBatchStatusStep({
                  step: LabelBatchStatusStep.Loading.ordinal,
                }),
              ),
              of(BulkActions.clearCostReview(jobGuid)),
              makeBulkCreateLabelObservable({
                action$,
                fulfillmentPlanIds: included,
                shipDate: shipDateAsUTCMidnight,
                getState,
              }).pipe(
                mergeMap(
                  handleBulkPurchaseProcess({
                    included,
                    excluded,
                    errored,
                    andPrint,
                    quickshipProps,
                    lbsModalId,
                    getState,
                  }),
                ),
                catchError((e) => {
                  const hasSubscriptionLimitError = BulkErrorsAndWarningsService.hasReceivedSubscriptionLimitError(e);
                  if (hasSubscriptionLimitError) {
                    return concat(
                      of(BulkActions.bulkPurchaseLabelFailure(e)),
                      of(
                        BulkActions.bulkSetActiveLabelBatchStatusStep({
                          step: LabelBatchStatusStep.SubscriptionLimitError.ordinal,
                        }),
                      ),
                    );
                  }

                  if (BulkErrorsAndWarningsService.hasCreateLabelErrorWithFulfillmentPlanContext(e)) {
                    const shaped = BulkErrorsAndWarningsService.mapCreateLabelErrorWithFulfillmentPlanContextToShape(
                      e.response.data.Errors,
                      getState,
                    );
                    return of(
                      BulkActions.bulkResetForErrorsAndWarnings(jobGuid, included, excluded, andPrint),
                      BulkActions.bulkUpdateJobErrorAndWarnings(jobGuid, shaped),
                      BulkActions.bulkSetActiveLabelBatchStatusStep({
                        step: LabelBatchStatusStep.ErrorOrWarning.ordinal,
                      }),
                      BulkActions.bulkPurchaseLabelFailure(e),
                    );
                  }

                  BulkPurchaseNotificationService.showGenericHttpNotification();
                  const modalId = Global.selectBulkLbsModalId(getState());
                  if (lbsModalId) {
                    ModalService.closeModalById(modalId);
                  }
                  return concat(
                    of(Actions.endBulkPurchase(included, excluded, errored, andPrint)),
                    of(BulkActions.bulkPurchaseLabelFailure(e)),
                    of(Actions.setLbsModalId('')),
                  );
                }),
              ),
            );
          }),
        ),
        waitForEndOfBulkPurchase({ action$ }).pipe(
          tap(() => {
            // just in case it's still around
            NotificationService.removeNotification(processingNotificationId);
          }),
          mergeMap(handleBulkPurchaseEnd),
        ),
      );
    }),
  );
};
